import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { SnackbarService } from '../../snackbare/snackbar.service';
import { HTTPTransactionService } from '../../http/http-transaction.service';

export const XTransactionName = new HttpContextToken<string>(() => '');
export const XTransactionTime = new HttpContextToken<string>(() => '');

// Add custom error message context token
export const XCustomErrorMessage = new HttpContextToken<string | undefined>(() => undefined);

@Injectable({
  providedIn: 'root',
})
export class HttpRequestInterceptor implements HttpInterceptor {
  constructor(
    private readonly httpTransactionService: HTTPTransactionService,
    private readonly snackbarService: SnackbarService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const transactionContext = request.context.get(XTransactionName);
    const transactionTime = request.context.get(XTransactionTime);
    this.httpTransactionService.clearErrorTransactions();
    if (transactionContext) {
      const transactionTimeString = transactionTime ? `:${transactionTime}` : '';
      this.httpTransactionService.addHttpRequestState(`${transactionContext}${transactionTimeString}`);
    }

    // Pass the modified request to the next interceptor or handler
    return next.handle(request).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        console.log('HTTP Error Interceptor:', errorResponse);
        // Get custom error message from context
        const customErrorMessage = request.context.get(XCustomErrorMessage);
        if (transactionContext) {
          const transactionTimeString = transactionTime ? `:${transactionTime}` : '';
          this.httpTransactionService.addErrorTransaction(`${transactionContext}${transactionTimeString}`);
        }
        this.snackbarService.showError({
          message: customErrorMessage || errorResponse.error?.message || 'An error occurred',
        });
        const sec = errorResponse.error as {
          code: number;
          key: string;
          msg?: string;
          data?: any;
          fields?: Record<string, string>;
          retry?: boolean;
          corr: string;
        };
        if (sec?.code && sec?.key && sec?.corr) {
          const message = sec.msg ?? sec.key ?? this.fallback(errorResponse.status);
          this.snackbarService.showError({ message, duration: 4000, action: 'OK' });

          return throwError(() => sec);
        }
        this.snackbarService.showError({ message: this.fallback(errorResponse.status), duration: 4000, action: 'OK' });

        return throwError(() => errorResponse);
      }),
      finalize(() => {
        const transactionContext = request.context.get(XTransactionName);
        const transactionTime = request.context.get(XTransactionTime);

        if (!transactionContext) {
          return;
        }

        const transactionTimeString = transactionTime ? `:${transactionTime}` : '';
        this.httpTransactionService.removeHttpRequestState(`${transactionContext}${transactionTimeString}`);
      })
    );
  }

  private fallback(status?: number) {
    switch (status) {
      case 401:
        return 'Bitte erneut anmelden.';
      case 403:
        return 'Zugriff verweigert.';
      case 404:
        return 'Nicht gefunden.';
      case 422:
        return 'Bitte Eingaben prüfen.';
      case 429:
        return 'Zu viele Anfragen – später erneut versuchen.';
      default:
        return 'Es ist ein Fehler aufgetreten.';
    }
  }
}
